// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export type MeasurementState =
  | "connected"
  | "degraded"
  | "dns_failure"
  | "internet_unreachable"
  | "gateway_unreachable"
  | "interface_down"
  | "server_unreachable"
  | "unknown";

export type MeasurementTimeQuality = "sntp" | "server" | "unanchored" | "reconstructed";
export type InternetAvailabilityBucketDuration = string;
export type InternetAvailabilityBucketStatus = "connected" | "degraded" | "interrupted" | "unknown";
export type InternetAvailabilitySummaryPeriod = "day" | "week" | "month";
export type InternetIncidentState =
  | "dns_failure"
  | "internet_unreachable"
  | "gateway_unreachable"
  | "interface_down"
  | "multiple";
export type InternetIncidentClassification = "expected_provider_reconnect" | "unexpected";

export interface ProbeTimelinePoint {
  readonly measurementId: string;
  readonly measuredAt: string;
  readonly receivedAt: string;
  readonly timeQuality: MeasurementTimeQuality;
  readonly timeUncertaintyMs: number | null;
  readonly state: MeasurementState;
  readonly sequenceNumber: number;
  readonly probeDurationMs: number | null;
  readonly configurationVersion: number;
}

export interface ProbeTimeline {
  readonly probeId: string;
  readonly displayName: string;
  readonly points: readonly ProbeTimelinePoint[];
}

export interface TenantMeasurementTimeline {
  readonly from: string;
  readonly to: string;
  readonly probes: readonly ProbeTimeline[];
}

export interface ProbeLatencyPoint {
  readonly startedAt: string;
  readonly endedAt: string;
  readonly sampleCount: number;
  readonly status: "connected" | "degraded" | "interrupted" | "unknown";
  readonly gatewayLatencyMs: number | null;
  readonly dnsLatencyMs: number | null;
  readonly primaryHttpLatencyMs: number | null;
}

export interface ProbeLatencyTimeline {
  readonly probeId: string;
  readonly displayName: string;
  readonly points: readonly ProbeLatencyPoint[];
}

export interface TenantLatencyTimeline {
  readonly from: string;
  readonly to: string;
  readonly pageFrom: string;
  readonly pageTo: string;
  readonly bucketDuration: InternetAvailabilityBucketDuration;
  readonly pageSize: number;
  readonly nextCursor: string | null;
  readonly probes: readonly ProbeLatencyTimeline[];
}

export interface InternetAvailabilityWindowSummary {
  readonly period: InternetAvailabilitySummaryPeriod;
  readonly from: string;
  readonly to: string;
  readonly availabilityPercent: number | null;
  readonly coveragePercent: number;
  readonly knownDurationMs: number;
  readonly connectedDurationMs: number;
  readonly degradedDurationMs: number;
  readonly interruptedDurationMs: number;
  readonly unknownDurationMs: number;
  readonly interruptionCount: number;
}

export interface InternetAvailabilityBucket {
  readonly startedAt: string;
  readonly endedAt: string;
  readonly status: InternetAvailabilityBucketStatus;
  readonly availabilityPercent: number | null;
  readonly coveragePercent: number;
  readonly sampleCount: number;
  readonly hasReclassifiedMeasurements: boolean;
  readonly knownDurationMs: number;
  readonly connectedDurationMs: number;
  readonly degradedDurationMs: number;
  readonly interruptedDurationMs: number;
  readonly unknownDurationMs: number;
  readonly interruptionCount: number;
}

export interface ProbeInternetAvailability {
  readonly probeId: string;
  readonly displayName: string;
  readonly summaries: readonly InternetAvailabilityWindowSummary[];
  readonly buckets: readonly InternetAvailabilityBucket[];
}

export interface TenantInternetAvailability {
  readonly from: string;
  readonly to: string;
  readonly pageFrom: string;
  readonly pageTo: string;
  readonly bucketDuration: InternetAvailabilityBucketDuration;
  readonly pageSize: number;
  readonly nextCursor: string | null;
  readonly probes: readonly ProbeInternetAvailability[];
}

export interface InternetIncident {
  readonly startedAt: string;
  readonly endedAt: string;
  readonly durationMs: number;
  readonly state: InternetIncidentState;
  readonly sampleCount: number;
  readonly classification: InternetIncidentClassification;
}

export interface ProbeInternetIncidentLog {
  readonly probeId: string;
  readonly displayName: string;
  readonly from: string;
  readonly to: string;
  readonly incidents: readonly InternetIncident[];
}

const STATES: ReadonlySet<string> = new Set<string>([
  "connected",
  "degraded",
  "dns_failure",
  "internet_unreachable",
  "gateway_unreachable",
  "interface_down",
  "server_unreachable",
  "unknown",
]);
const TIME_QUALITIES: ReadonlySet<string> = new Set<string>(["sntp", "server", "unanchored", "reconstructed"]);
const BUCKET_STATUSES: ReadonlySet<string> = new Set<string>(["connected", "degraded", "interrupted", "unknown"]);
const SUMMARY_PERIODS: ReadonlySet<string> = new Set<string>(["day", "week", "month"]);
const INCIDENT_STATES: ReadonlySet<string> = new Set<string>([
  "dns_failure",
  "internet_unreachable",
  "gateway_unreachable",
  "interface_down",
  "multiple",
]);
const INCIDENT_CLASSIFICATIONS: ReadonlySet<string> = new Set<string>(["expected_provider_reconnect", "unexpected"]);
const LATENCY_STATUSES: ReadonlySet<string> = new Set<string>(["connected", "degraded", "interrupted", "unknown"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Timeline response field ${fieldName} is invalid`);
  }
  return value;
}

function readNumber(value: unknown, fieldName: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Timeline response field ${fieldName} is invalid`);
  }
  return value;
}

function readNullableNumber(value: unknown, fieldName: string): number | null {
  if (value === null) {
    return null;
  }
  return readNumber(value, fieldName);
}

function readAvailabilityPercent(value: unknown, fieldName: string): number | null {
  if (value === null) {
    return null;
  }
  return readNumber(value, fieldName);
}

function readPoint(value: unknown, fieldName: string): ProbeTimelinePoint {
  if (!isRecord(value)) {
    throw new Error(`Timeline response field ${fieldName} is invalid`);
  }
  const state: string = readString(value.state, `${fieldName}.state`);
  if (!STATES.has(state)) {
    throw new Error(`Timeline response field ${fieldName}.state is invalid`);
  }
  const timeQuality: string = readString(value.timeQuality, `${fieldName}.timeQuality`);
  if (!TIME_QUALITIES.has(timeQuality)) {
    throw new Error(`Timeline response field ${fieldName}.timeQuality is invalid`);
  }
  return {
    measurementId: readString(value.measurementId, `${fieldName}.measurementId`),
    measuredAt: readString(value.measuredAt, `${fieldName}.measuredAt`),
    receivedAt: readString(value.receivedAt, `${fieldName}.receivedAt`),
    timeQuality: timeQuality as MeasurementTimeQuality,
    timeUncertaintyMs: readNullableNumber(value.timeUncertaintyMs, `${fieldName}.timeUncertaintyMs`),
    state: state as MeasurementState,
    sequenceNumber: readNumber(value.sequenceNumber, `${fieldName}.sequenceNumber`),
    probeDurationMs: readNullableNumber(value.probeDurationMs, `${fieldName}.probeDurationMs`),
    configurationVersion: readNumber(value.configurationVersion, `${fieldName}.configurationVersion`),
  };
}

function readSummary(value: unknown, fieldName: string): InternetAvailabilityWindowSummary {
  if (!isRecord(value)) {
    throw new Error(`Internet availability response field ${fieldName} is invalid`);
  }
  const period: string = readString(value.period, `${fieldName}.period`);
  if (!SUMMARY_PERIODS.has(period)) {
    throw new Error(`Internet availability response field ${fieldName}.period is invalid`);
  }
  return {
    period: period as InternetAvailabilitySummaryPeriod,
    from: readString(value.from, `${fieldName}.from`),
    to: readString(value.to, `${fieldName}.to`),
    availabilityPercent: readAvailabilityPercent(value.availabilityPercent, `${fieldName}.availabilityPercent`),
    coveragePercent: readNumber(value.coveragePercent, `${fieldName}.coveragePercent`),
    knownDurationMs: readNumber(value.knownDurationMs, `${fieldName}.knownDurationMs`),
    connectedDurationMs: readNumber(value.connectedDurationMs, `${fieldName}.connectedDurationMs`),
    degradedDurationMs: readNumber(value.degradedDurationMs, `${fieldName}.degradedDurationMs`),
    interruptedDurationMs: readNumber(value.interruptedDurationMs, `${fieldName}.interruptedDurationMs`),
    unknownDurationMs: readNumber(value.unknownDurationMs, `${fieldName}.unknownDurationMs`),
    interruptionCount: readNumber(value.interruptionCount, `${fieldName}.interruptionCount`),
  };
}

function readBucket(value: unknown, fieldName: string): InternetAvailabilityBucket {
  if (!isRecord(value)) {
    throw new Error(`Internet availability response field ${fieldName} is invalid`);
  }
  const status: string = readString(value.status, `${fieldName}.status`);
  if (!BUCKET_STATUSES.has(status)) {
    throw new Error(`Internet availability response field ${fieldName}.status is invalid`);
  }
  return {
    startedAt: readString(value.startedAt, `${fieldName}.startedAt`),
    endedAt: readString(value.endedAt, `${fieldName}.endedAt`),
    status: status as InternetAvailabilityBucketStatus,
    availabilityPercent: readAvailabilityPercent(value.availabilityPercent, `${fieldName}.availabilityPercent`),
    coveragePercent: readNumber(value.coveragePercent, `${fieldName}.coveragePercent`),
    sampleCount: readNumber(value.sampleCount, `${fieldName}.sampleCount`),
    hasReclassifiedMeasurements: value.hasReclassifiedMeasurements === true,
    knownDurationMs: readNumber(value.knownDurationMs, `${fieldName}.knownDurationMs`),
    connectedDurationMs: readNumber(value.connectedDurationMs, `${fieldName}.connectedDurationMs`),
    degradedDurationMs: readNumber(value.degradedDurationMs, `${fieldName}.degradedDurationMs`),
    interruptedDurationMs: readNumber(value.interruptedDurationMs, `${fieldName}.interruptedDurationMs`),
    unknownDurationMs: readNumber(value.unknownDurationMs, `${fieldName}.unknownDurationMs`),
    interruptionCount: readNumber(value.interruptionCount, `${fieldName}.interruptionCount`),
  };
}

function readInternetAvailabilityProbe(value: unknown, fieldName: string): ProbeInternetAvailability {
  if (!isRecord(value) || !Array.isArray(value.summaries) || !Array.isArray(value.buckets)) {
    throw new Error(`Internet availability response field ${fieldName} is invalid`);
  }
  return {
    probeId: readString(value.probeId, `${fieldName}.probeId`),
    displayName: readString(value.displayName, `${fieldName}.displayName`),
    summaries: value.summaries.map((summary: unknown, index: number): InternetAvailabilityWindowSummary =>
      readSummary(summary, `${fieldName}.summaries[${index}]`),
    ),
    buckets: value.buckets.map((bucket: unknown, index: number): InternetAvailabilityBucket =>
      readBucket(bucket, `${fieldName}.buckets[${index}]`),
    ),
  };
}

function readInternetAvailability(payload: unknown): TenantInternetAvailability {
  if (!isRecord(payload) || !Array.isArray(payload.probes)) {
    throw new Error("Internet availability response is invalid");
  }
  return {
    from: readString(payload.from, "from"),
    to: readString(payload.to, "to"),
    pageFrom: readString(payload.pageFrom, "pageFrom"),
    pageTo: readString(payload.pageTo, "pageTo"),
    bucketDuration: readString(payload.bucketDuration, "bucketDuration"),
    pageSize: readNumber(payload.pageSize, "pageSize"),
    nextCursor: payload.nextCursor === null ? null : readString(payload.nextCursor, "nextCursor"),
    probes: payload.probes.map((probe: unknown, index: number): ProbeInternetAvailability =>
      readInternetAvailabilityProbe(probe, `probes[${index}]`),
    ),
  };
}

function readIncident(value: unknown, fieldName: string): InternetIncident {
  if (!isRecord(value)) {
    throw new Error(`Internet incident response field ${fieldName} is invalid`);
  }
  const state: string = readString(value.state, `${fieldName}.state`);
  if (!INCIDENT_STATES.has(state)) {
    throw new Error(`Internet incident response field ${fieldName}.state is invalid`);
  }
  const classification: string = readString(value.classification, `${fieldName}.classification`);
  if (!INCIDENT_CLASSIFICATIONS.has(classification)) {
    throw new Error(`Internet incident response field ${fieldName}.classification is invalid`);
  }
  return {
    startedAt: readString(value.startedAt, `${fieldName}.startedAt`),
    endedAt: readString(value.endedAt, `${fieldName}.endedAt`),
    durationMs: readNumber(value.durationMs, `${fieldName}.durationMs`),
    state: state as InternetIncidentState,
    sampleCount: readNumber(value.sampleCount, `${fieldName}.sampleCount`),
    classification: classification as InternetIncidentClassification,
  };
}

function readIncidentLog(payload: unknown): ProbeInternetIncidentLog {
  if (!isRecord(payload) || !Array.isArray(payload.incidents)) {
    throw new Error("Internet incident response is invalid");
  }
  return {
    probeId: readString(payload.probeId, "probeId"),
    displayName: readString(payload.displayName, "displayName"),
    from: readString(payload.from, "from"),
    to: readString(payload.to, "to"),
    incidents: payload.incidents.map((incident: unknown, index: number): InternetIncident =>
      readIncident(incident, `incidents[${index}]`),
    ),
  };
}

function readProbeTimeline(value: unknown, fieldName: string): ProbeTimeline {
  if (!isRecord(value) || !Array.isArray(value.points)) {
    throw new Error(`Timeline response field ${fieldName} is invalid`);
  }
  return {
    probeId: readString(value.probeId, `${fieldName}.probeId`),
    displayName: readString(value.displayName, `${fieldName}.displayName`),
    points: value.points.map((point: unknown, index: number): ProbeTimelinePoint =>
      readPoint(point, `${fieldName}.points[${index}]`),
    ),
  };
}

function readTimeline(payload: unknown): TenantMeasurementTimeline {
  if (!isRecord(payload) || !Array.isArray(payload.probes)) {
    throw new Error("Timeline response is invalid");
  }
  return {
    from: readString(payload.from, "from"),
    to: readString(payload.to, "to"),
    probes: payload.probes.map((probe: unknown, index: number): ProbeTimeline =>
      readProbeTimeline(probe, `probes[${index}]`),
    ),
  };
}

function readLatencyPoint(value: unknown, fieldName: string): ProbeLatencyPoint {
  if (!isRecord(value)) throw new Error(`Latency response field ${fieldName} is invalid`);
  const status: string = readString(value.status, `${fieldName}.status`);
  if (!LATENCY_STATUSES.has(status)) throw new Error(`Latency response field ${fieldName}.status is invalid`);
  return {
    startedAt: readString(value.startedAt, `${fieldName}.startedAt`),
    endedAt: readString(value.endedAt, `${fieldName}.endedAt`),
    sampleCount: readNumber(value.sampleCount, `${fieldName}.sampleCount`),
    status: status as ProbeLatencyPoint["status"],
    gatewayLatencyMs: readNullableNumber(value.gatewayLatencyMs, `${fieldName}.gatewayLatencyMs`),
    dnsLatencyMs: readNullableNumber(value.dnsLatencyMs, `${fieldName}.dnsLatencyMs`),
    primaryHttpLatencyMs: readNullableNumber(value.primaryHttpLatencyMs, `${fieldName}.primaryHttpLatencyMs`),
  };
}

function readLatencyTimeline(payload: unknown): TenantLatencyTimeline {
  if (!isRecord(payload) || !Array.isArray(payload.probes)) throw new Error("Latency response is invalid");
  return {
    from: readString(payload.from, "from"),
    to: readString(payload.to, "to"),
    pageFrom: readString(payload.pageFrom, "pageFrom"),
    pageTo: readString(payload.pageTo, "pageTo"),
    bucketDuration: readString(payload.bucketDuration, "bucketDuration"),
    pageSize: readNumber(payload.pageSize, "pageSize"),
    nextCursor: payload.nextCursor === null ? null : readString(payload.nextCursor, "nextCursor"),
    probes: payload.probes.map((probe: unknown, index: number): ProbeLatencyTimeline => {
      if (!isRecord(probe) || !Array.isArray(probe.points)) {
        throw new Error(`Latency response field probes[${index}] is invalid`);
      }
      return {
        probeId: readString(probe.probeId, `probes[${index}].probeId`),
        displayName: readString(probe.displayName, `probes[${index}].displayName`),
        points: probe.points.map((point: unknown, pointIndex: number): ProbeLatencyPoint =>
          readLatencyPoint(point, `probes[${index}].points[${pointIndex}]`),
        ),
      };
    }),
  };
}

export async function loadTenantMeasurementTimeline(
  tenantId: string,
  hours: number = 24,
): Promise<TenantMeasurementTimeline> {
  return readTimeline(
    await requestJson(
      `/api/v1/tenants/${encodeURIComponent(tenantId)}/measurements/timeline?hours=${encodeURIComponent(String(hours))}`,
    ),
  );
}

export async function loadTenantLatencyTimeline(
  tenantId: string,
  from: string,
  to: string,
  bucketDuration: InternetAvailabilityBucketDuration,
  cursor: string | null = null,
): Promise<TenantLatencyTimeline> {
  const cursorQuery: string = cursor === null ? "" : `&cursor=${encodeURIComponent(cursor)}`;
  return readLatencyTimeline(
    await requestJson(
      `/api/v1/tenants/${encodeURIComponent(tenantId)}/latency-timeline`
      + `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucketDuration=${encodeURIComponent(bucketDuration)}${cursorQuery}`,
    ),
  );
}

export async function loadTenantInternetAvailability(
  tenantId: string,
  from: string,
  to: string,
  bucketDuration: InternetAvailabilityBucketDuration,
  cursor: string | null = null,
  probeId: string | null = null,
  includeBuckets: boolean = true,
): Promise<TenantInternetAvailability> {
  const cursorQuery: string = cursor === null ? "" : `&cursor=${encodeURIComponent(cursor)}`;
  const probeQuery: string = probeId === null ? "" : `&probeId=${encodeURIComponent(probeId)}`;
  return readInternetAvailability(
    await requestJson(
      `/api/v1/tenants/${encodeURIComponent(tenantId)}/internet-availability`
      + `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucketDuration=${encodeURIComponent(bucketDuration)}&pageSize=120&includeBuckets=${String(includeBuckets)}${cursorQuery}${probeQuery}`,
    ),
  );
}

export async function loadProbeInternetIncidents(
  tenantId: string,
  probeId: string,
  from: string,
  to: string,
  limit: number = 200,
): Promise<ProbeInternetIncidentLog> {
  return readIncidentLog(
    await requestJson(
      `/api/v1/tenants/${encodeURIComponent(tenantId)}/probes/${encodeURIComponent(probeId)}/internet-incidents`
      + `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=${encodeURIComponent(String(limit))}`,
    ),
  );
}
